import { useEffect, useState } from 'react';
import { Button } from '../../../../shared/ui/button';
import { Input } from '../../../../shared/ui/input';
import { Label } from '../../../../shared/ui/label';
import { Switch } from '../../../../shared/ui/switch';
import { Checkbox } from '../../../../shared/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../../../shared/ui/radio-group';
import { DropdownOptions } from '../../../../shared/ui/dropdown-options';
import { sectionHeading, cardTitle, cardDescription, cardPadding, saveButton, segmentedTrack, segmentedTab } from '../profileStyles';
import { StripeField } from '../../../../shared/ui/stripe-field';
import { fieldClassName, labelClassName, primaryButtonClassName, dialogFooterClassName, cancelButtonClassName, cardSurfaceClassName } from '../../../../shared/ui/formStyles';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../shared/ui/dialog';
import { CreditCard, Landmark, Trash2, Loader2, AlertCircle } from 'lucide-react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';
import { VerifyAchDialog } from './VerifyAchDialog';
import { billingApi } from '../../../../shared/services/api/billingApi';
import { isMockPaymentsMode, collectBankAccountForSetup } from '../../../../shared/lib/stripeJs';
import { toast } from '../../../../shared/services/toast';
import { PaymentMethodSelector } from '../../../billing/components/PaymentMethodSelector';
import { useRenewalPreference } from '../../../../shared/hooks/useRenewalPreference';
import { useStripeElementStyle } from '../../../../shared/hooks/useStripeElementStyle';
import { checkboxLabelClassName } from '../../../../shared/ui/formStyles';

// PaymentMethodType + PaymentMethodStatus mirror Billing.Domain.Enums — keep in sync.
const METHOD_CARD = 1;
const METHOD_ACH = 2;
const STATUS_ACTIVE = 1;
const STATUS_PENDING_VERIFICATION = 2;
const STATUS_EXPIRED = 3;
const STATUS_INACTIVE = 4;

const statusLabel = (status) => {
  switch (status) {
    case STATUS_ACTIVE:
      return 'Verified';
    case STATUS_PENDING_VERIFICATION:
      return 'Verification Required';
    case STATUS_EXPIRED:
      return 'Expired';
    case STATUS_INACTIVE:
      return 'Inactive';
    default:
      return 'Unknown';
  }
};

const formatExpiry = (month, year) => {
  if (!month || !year) return '';
  const mm = String(month).padStart(2, '0');
  const yy = String(year).slice(-2);
  return `${mm}/${yy}`;
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const friendlyAddError = (err) => {
  const status = err?.response?.status;
  const code = err?.response?.data?.errorCode;
  if (status === 409 && code === 'STRIPE_CUSTOMER_NOT_PROVISIONED') {
    return 'Your billing account is still being set up. Please try again in a minute.';
  }
  if (status === 403 && code === 'CROSS_FIRM_REPLAY') {
    return 'That payment method does not belong to this account.';
  }
  if (status === 429) {
    return 'Too many requests. Please wait a minute and try again.';
  }
  return err?.response?.data?.message || err?.message || 'Failed to save payment method.';
};

// Obtain a Stripe PaymentMethod id (+ SetupIntent id) for the chosen ACH verification path.
// Returns { paymentMethodId, setupIntentId } on success, or null when the caller should abort
// (Stripe not ready, or the collection/confirmation surfaced an error toast).
async function collectAchPaymentMethod({ stripe, clientSecret, achVerificationMethod, bankData }) {
  if (isMockPaymentsMode()) {
    return { paymentMethodId: `pm_mock_${Date.now()}`, setupIntentId: `seti_mock_${Date.now()}` };
  }
  if (!stripe) {
    toast.error('Stripe.js is still loading. Please try again in a moment.');
    return null;
  }

  if (achVerificationMethod === 'instant') {
    // Financial Connections: customer authenticates with their bank in Stripe's hosted UI.
    // Returns a verified PaymentMethod if the bank is supported; falls back to micro-deposits
    // for unsupported banks (in which case the saved method appears as pending).
    const result = await collectBankAccountForSetup(stripe, clientSecret, {
      name: bankData.accountHolderName || undefined,
    });
    if (result.error) {
      toast.error(result.error.message || 'Failed to connect bank account.');
      return null;
    }
    return { paymentMethodId: result.paymentMethodId, setupIntentId: result.setupIntentId };
  }

  const { setupIntent, error: confirmError } = await stripe.confirmUsBankAccountSetup(clientSecret, {
    payment_method: {
      us_bank_account: {
        account_holder_type: bankData.accountHolderType,
        account_type: bankData.accountType,
        routing_number: bankData.routingNumber,
        account_number: bankData.accountNumber,
      },
      billing_details: { name: bankData.accountHolderName },
    },
  });
  if (confirmError) {
    toast.error(confirmError.message || 'Failed to add bank account.');
    return null;
  }
  return { paymentMethodId: setupIntent.payment_method, setupIntentId: setupIntent.id };
}

function buildAchSavePayload({ collected, makeDefault, achMandate }) {
  const payload = {
    stripePaymentMethodId: collected.paymentMethodId,
    methodType: METHOD_ACH,
    makeDefault,
    stripeSetupIntentId: collected.setupIntentId,
  };
  if (achMandate) {
    payload.achMandate = {
      hash: achMandate.hash,
      acceptedAtUtc: new Date().toISOString(),
    };
  }
  return payload;
}

function PaymentMethodRow({ method, onVerify, onMakePrimary, onDelete }) {
  const accountTypeSuffix = method.accountType ? ` ${capitalize(method.accountType)}` : '';
  const pendingSuffix = method.status === STATUS_PENDING_VERIFICATION ? ' — ' + statusLabel(method.status) : '';
  const methodLabel = method.methodType === METHOD_CARD
    ? `${capitalize(method.brand) || 'Card'} ending in ${method.lastFour}`
    : `${method.bankName || 'Bank Account'}${accountTypeSuffix}`;
  const methodDetails = method.methodType === METHOD_CARD
    ? `Expires ${formatExpiry(method.expMonth, method.expYear)}`
    : `Account ending in ${method.lastFour}${pendingSuffix}`;
  const showVerify = method.methodType === METHOD_ACH && method.status === STATUS_PENDING_VERIFICATION;
  const showMakePrimary = !method.isDefault && method.status === STATUS_ACTIVE;
  return (
    <div
      className={`${cardPadding} rounded-xl border transition-all flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 ${
        // Same card colours as everywhere else; the primary method swaps only the border to brand.
        method.isDefault ? 'border-brand bg-surface-panel' : cardSurfaceClassName
      }`}
      data-testid={`profile-payment-method-item-${method.token}`}
    >
      <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0 w-full sm:w-auto">
        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl flex items-center justify-center bg-chip-info-bg flex-shrink-0" data-testid={`profile-payment-method-icon-${method.token}`}>
          {method.methodType === METHOD_CARD ? (
            <CreditCard className="text-brand w-5 h-5 sm:w-6 sm:h-6" />
          ) : (
            <Landmark className="text-brand w-5 h-5 sm:w-6 sm:h-6" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`${cardTitle} truncate`} data-testid={`profile-payment-method-label-${method.token}`}>
              {methodLabel}
            </span>
          </div>
          <p className={cardDescription} data-testid={`profile-payment-method-details-${method.token}`}>
            {methodDetails}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 flex-wrap w-full sm:w-auto" data-testid={`profile-payment-method-actions-${method.token}`}>
        {method.isDefault && (
          <span className="text-xs font-medium text-chip-info-fg bg-chip-info-bg px-2 sm:px-3 py-1 rounded" data-testid={`profile-payment-method-primary-badge-${method.token}`}>
            Primary
          </span>
        )}
        {showVerify && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onVerify(method)}
            className="text-brand border-brand hover:bg-surface-hover text-xs h-7 sm:h-8 px-2 sm:px-3"
            data-testid={`profile-payment-method-verify-button-${method.token}`}
          >
            Verify
          </Button>
        )}
        {showMakePrimary && (
          <button
            onClick={() => onMakePrimary(method.token)}
            className="text-xs sm:text-sm text-brand hover:underline font-medium"
            data-testid={`profile-payment-method-make-primary-button-${method.token}`}
          >
            Make Primary
          </button>
        )}
        <button
          onClick={() => onDelete(method)}
          className="text-danger-fg hover:text-danger-fg p-1"
          title="Remove Method"
          data-testid={`profile-payment-method-delete-button-${method.token}`}
        >
          <Trash2 size={16} className="sm:w-[18px] sm:h-[18px]" />
        </button>
      </div>
    </div>
  );
}

function PaymentMethodsList({ paymentMethods, activeTab, loading, onVerify, onMakePrimary, onDelete }) {
  const filteredMethods = paymentMethods.filter((m) =>
    activeTab === 'card' ? m.methodType === METHOD_CARD : m.methodType === METHOD_ACH
  );

  if (filteredMethods.length === 0 && !loading) {
    return (
      <div className="text-center py-12 border-2 border-dashed border-line rounded-2xl" data-testid="profile-payment-empty">
        {activeTab === 'card' ? (
          <CreditCard className="h-12 w-12 text-fg-muted mx-auto mb-4" />
        ) : (
          <Landmark className="h-12 w-12 text-fg-muted mx-auto mb-4" />
        )}
        <p className="text-fg font-semibold" data-testid="profile-payment-empty-title">
          No {activeTab === 'card' ? 'cards' : 'bank accounts'} saved
        </p>
        <p className="text-fg-muted text-sm mt-1" data-testid="profile-payment-empty-desc">
          Add a {activeTab === 'card' ? 'card' : 'bank account'} below to get started
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:gap-4" data-testid="profile-payment-methods-list">
      {filteredMethods.map((method) => (
        <PaymentMethodRow
          key={method.token}
          method={method}
          onVerify={onVerify}
          onMakePrimary={onMakePrimary}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

function DeletePaymentDialog({ deletingMethod, onOpenChange, onCancel, onConfirm, isDeleting }) {
  return (
    <Dialog open={!!deletingMethod} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="profile-delete-payment-modal">
        <DialogHeader>
          <DialogTitle data-testid="profile-delete-payment-modal-title">
            Delete Payment Method
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-fg-secondary" data-testid="profile-delete-payment-modal-desc">
            Are you sure you want to delete this payment method?
          </p>
          {deletingMethod && (
            <div className="mt-4 p-3 bg-surface-field rounded-xl" data-testid="profile-delete-payment-modal-item">
              <p className="text-sm font-semibold text-fg" data-testid="profile-delete-payment-modal-item-label">
                {deletingMethod.methodType === METHOD_CARD
                  ? `${capitalize(deletingMethod.brand) || 'Card'} ending in ${deletingMethod.lastFour}`
                  : `${deletingMethod.bankName || 'Bank Account'} ending in ${deletingMethod.lastFour}`}
              </p>
              {deletingMethod.methodType === METHOD_CARD && (
                <p className="text-xs text-fg-muted mt-1" data-testid="profile-delete-payment-modal-item-expiry">
                  Expires {formatExpiry(deletingMethod.expMonth, deletingMethod.expYear)}
                </p>
              )}
            </div>
          )}
          <p className="text-xs text-danger-fg mt-4" data-testid="profile-delete-payment-modal-info">This action cannot be undone.</p>
        </div>
        <DialogFooter className={`${dialogFooterClassName} !px-0`}>
          <Button variant="outline" className={cancelButtonClassName} onClick={onCancel} disabled={isDeleting} data-testid="profile-delete-payment-modal-cancel">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isDeleting}
            className={`${primaryButtonClassName} bg-danger-fg hover:bg-danger-fg/90`}
            data-testid="profile-delete-payment-modal-confirm"
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" data-testid="profile-delete-payment-modal-spinner" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EnableAutoRenewalDialog({
  open,
  onOpenChange,
  onCancel,
  autoRenewal,
  enableTokenChoice,
  setEnableTokenChoice,
  savingAutoRenewal,
  onConfirm,
  enableAutoRenewalLabel,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="profile-auto-renewal-enable-modal">
        <DialogHeader>
          <DialogTitle data-testid="profile-auto-renewal-enable-modal-title">
            {autoRenewal.enabled ? 'Change Payment Method' : 'Enable Auto-Renewal'}
          </DialogTitle>
        </DialogHeader>
        <div className="py-2 space-y-3">
          <p className="text-sm text-fg-secondary" data-testid="profile-auto-renewal-enable-modal-desc">
            {autoRenewal.enabled
              ? 'Select a different payment method for auto-renewal. We will update your billing to use this method for future charges.'
              : 'Pick the payment method we should use to auto-charge future renewals. We will sync your choice to billing so invoices are charged automatically.'}
          </p>
          <PaymentMethodSelector
            value={enableTokenChoice}
            onChange={setEnableTokenChoice}
            preferDefault
            disabled={savingAutoRenewal}
            dataTestId="profile-auto-renewal-enable-modal-selector"
          />
        </div>
        <DialogFooter className={`${dialogFooterClassName} !px-0`}>
          <Button variant="outline" className={cancelButtonClassName} onClick={onCancel} disabled={savingAutoRenewal} data-testid="profile-auto-renewal-enable-modal-cancel">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={savingAutoRenewal || !enableTokenChoice}
            className={primaryButtonClassName}
            data-testid="profile-auto-renewal-enable-modal-confirm"
          >
            {savingAutoRenewal ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" data-testid="profile-auto-renewal-enable-modal-spinner" />
                Saving…
              </>
            ) : (
              enableAutoRenewalLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisableAutoRenewalDialog({ open, onOpenChange, onCancel, onConfirm, savingAutoRenewal }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="profile-auto-renewal-disable-modal">
        <DialogHeader>
          <DialogTitle data-testid="profile-auto-renewal-disable-modal-title">
            Disable Auto-Renewal?
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-2 text-sm text-fg-secondary" data-testid="profile-auto-renewal-disable-modal-content">
          <p>Your subscriptions will no longer auto-charge.</p>
          <p>You&apos;ll receive an invoice instead, and need to pay manually before the due date.</p>
        </div>
        <DialogFooter className={`${dialogFooterClassName} !px-0`}>
          <Button variant="outline" className={cancelButtonClassName} onClick={onCancel} disabled={savingAutoRenewal} data-testid="profile-auto-renewal-disable-modal-cancel">
            Keep Enabled
          </Button>
          <Button
            onClick={onConfirm}
            disabled={savingAutoRenewal}
            className={`${primaryButtonClassName} bg-danger-fg hover:bg-danger-fg/90`}
            data-testid="profile-auto-renewal-disable-modal-confirm"
          >
            {savingAutoRenewal ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" data-testid="profile-auto-renewal-disable-modal-spinner" />
                Disabling…
              </>
            ) : (
              'Disable'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PaymentSection({ dataTestId }) {
  const stripe = useStripe();
  const elements = useElements();
  const stripeElementStyle = useStripeElementStyle();

  const {
    paymentMethods,
    loading,
    error,
    refetch,
    deletePaymentMethod,
    setDefaultPaymentMethod,
    verifyAch,
  } = usePaymentMethods();

  const [activeTab, setActiveTab] = useState('card');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verifyingMethod, setVerifyingMethod] = useState(null);
  const [deletingMethod, setDeletingMethod] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [bankData, setBankData] = useState({
    accountHolderName: '',
    routingNumber: '',
    accountNumber: '',
    accountHolderType: 'individual',
    accountType: 'checking',
  });
  const [makeDefault, setMakeDefault] = useState(false);

  // NACHA ACH debit-authorization mandate — fetched lazily when the ACH tab activates.
  // Hash is echoed back on save so the backend can verify the customer accepted exactly
  // the text we displayed. Backend rejects the save with `ach_mandate_required` otherwise.
  const [achMandate, setAchMandate] = useState(null);
  const [achMandateAccepted, setAchMandateAccepted] = useState(false);

  // Two parallel ACH verification paths the customer can choose:
  //   'instant' → Stripe Financial Connections (hosted bank-login UI, verified in seconds —
  //               works only for banks Stripe supports)
  //   'manual'  → routing + account numbers + micro-deposit verification (1-2 business days)
  // Default to 'instant' since it converts dramatically better when the bank is supported.
  const [achVerificationMethod, setAchVerificationMethod] = useState('instant');

  useEffect(() => {
    if (activeTab !== 'ach') return;
    if (achMandate) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await billingApi.getAchMandate();
        if (cancelled) return;
        setAchMandate(response?.data ?? null);
      } catch {
        if (cancelled) return;
        toast.error('Could not load the ACH authorization text. Please try again.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, achMandate]);

  // Auto-renewal toggle state (Phase 2). Backed by /api/billing/auto-renewal. Kept in the shared
  // renewalPreferenceStore (via useRenewalPreference) so RenewalOptions (subscription page)
  // reflects changes made here without a full page reload.
  const {
    renewalPreference,
    loading: autoRenewalLoading,
    error: autoRenewalError,
    errorCode: autoRenewalErrorCode,
    setRenewalPreference,
  } = useRenewalPreference();
  const [autoRenewalReboundError, setAutoRenewalReboundError] = useState(null);
  const [showEnableDialog, setShowEnableDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [enableTokenChoice, setEnableTokenChoice] = useState(null);
  const [savingAutoRenewal, setSavingAutoRenewal] = useState(false);

  useEffect(() => {
    if (autoRenewalErrorCode === 'STRIPE_CUSTOMER_REBOUND') {
      // Force-disabled server-side; surface a banner so the user re-confirms.
      setAutoRenewalReboundError(
        'Your billing identity changed in our records. Auto-renewal has been turned off — please re-enable it to confirm.'
      );
    } else if (autoRenewalError) {
      // Silent — wallet still works.
      console.warn('Auto-renewal lookup failed:', autoRenewalError);
    }
  }, [autoRenewalError, autoRenewalErrorCode]);

  // Receives a boolean from the shared Switch (was a change event from a raw checkbox).
  const onAutoRenewalToggle = async (wantsEnabled) => {

    setAutoRenewalReboundError(null);
    if (wantsEnabled) {
      // Preselect the suggestion (Stripe default → newest active) so the dialog opens with
      // a reasonable choice already highlighted.
      try {
        const res = await billingApi.getAutoRenewalSuggestion();
        const tok = res?.data?.suggestion?.token ?? null;
        setEnableTokenChoice(tok);
      } catch {
        setEnableTokenChoice(null);
      }
      setShowEnableDialog(true);
    } else {
      setShowDisableDialog(true);
    }
  };

  const confirmEnableAutoRenewal = async () => {
    if (!enableTokenChoice) {
      toast.error('Please pick a payment method to use for auto-renewal.');
      return;
    }
    setSavingAutoRenewal(true);
    try {
      const res = await billingApi.setAutoRenewal({ enabled: true, paymentMethodToken: enableTokenChoice });
      if (res?.success) {
        setRenewalPreference(res.data || { enabled: true, paymentMethod: null });
        const isChange = renewalPreference.enabled;
        toast.success(isChange ? 'Payment method updated. Future invoices will be charged with this method.' : 'Auto-renewal enabled. Future invoices will be charged automatically.');
        setShowEnableDialog(false);
      } else {
        toast.error(res?.message || 'Failed to update auto-renewal.');
      }
    } catch (err) {
      const code = err?.response?.data?.errorCode;
      if (code === 'STRIPE_CUSTOMER_REBOUND') {
        toast.error('Your billing identity changed. Please reload and try again.');
      } else if (code === 'STRIPE_CUSTOMER_NOT_PROVISIONED') {
        toast.error('Your billing account is still being set up. Please try again in a minute.');
      } else {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to update auto-renewal.');
      }
    } finally {
      setSavingAutoRenewal(false);
    }
  };

  const confirmDisableAutoRenewal = async () => {
    setSavingAutoRenewal(true);
    try {
      const res = await billingApi.setAutoRenewal({ enabled: false, paymentMethodToken: null });
      if (res?.success) {
        setRenewalPreference(res.data || { enabled: false, paymentMethod: null });
        toast.success('Auto-renewal disabled. You’ll receive an invoice instead of being charged automatically.');
        setShowDisableDialog(false);
      } else {
        toast.error(res?.message || 'Failed to disable auto-renewal.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to disable auto-renewal.');
    } finally {
      setSavingAutoRenewal(false);
    }
  };

  const resetCardForm = () => {
    setCardholderName('');
    elements?.getElement(CardNumberElement)?.clear();
    elements?.getElement(CardExpiryElement)?.clear();
    elements?.getElement(CardCvcElement)?.clear();
  };
  const resetBankForm = () => {
    setBankData({
      accountHolderName: '',
      routingNumber: '',
      accountNumber: '',
      accountHolderType: 'individual',
      accountType: 'checking',
    });
  };

  const handleConfirmDelete = async () => {
    if (!deletingMethod) return;
    setIsDeleting(true);
    const success = await deletePaymentMethod(deletingMethod.token);
    setIsDeleting(false);
    if (success) setDeletingMethod(null);
  };

  const submitCard = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const setupRes = await billingApi.createSetupIntent({ methodType: METHOD_CARD });
      const clientSecret = setupRes?.data?.clientSecret;
      if (!clientSecret) {
        toast.error('Failed to initialize payment setup.');
        return;
      }

      let stripePaymentMethodId;
      if (isMockPaymentsMode()) {
        stripePaymentMethodId = `pm_mock_${Date.now()}`;
      } else {
        if (!stripe || !elements) {
          toast.error('Stripe.js is still loading. Please try again in a moment.');
          return;
        }
        const cardNumber = elements.getElement(CardNumberElement);
        const { setupIntent, error: confirmError } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardNumber,
            billing_details: { name: cardholderName || undefined },
          },
        });
        if (confirmError) {
          toast.error(confirmError.message || 'Failed to add card.');
          return;
        }
        stripePaymentMethodId = setupIntent.payment_method;
      }

      const saveRes = await billingApi.savePaymentMethod({
        stripePaymentMethodId,
        methodType: METHOD_CARD,
        makeDefault,
      });
      if (saveRes?.success) {
        toast.success('Payment method added successfully.');
        resetCardForm();
        setMakeDefault(false);
        await refetch();
      } else {
        toast.error(saveRes?.message || 'Failed to save payment method.');
      }
    } catch (err) {
      toast.error(friendlyAddError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitBank = async (e) => {
    e.preventDefault();
    // Manual path requires a valid 9-digit routing number. Instant path collects bank details
    // through Stripe's hosted UI, so we don't validate the fields locally.
    if (achVerificationMethod === 'manual' && bankData.routingNumber.length !== 9) {
      toast.error('Routing number must be 9 digits.');
      return;
    }
    setIsSubmitting(true);
    try {
      const setupRes = await billingApi.createSetupIntent({ methodType: METHOD_ACH });
      const clientSecret = setupRes?.data?.clientSecret;
      if (!clientSecret) {
        toast.error('Failed to initialize payment setup.');
        return;
      }

      const collected = await collectAchPaymentMethod({ stripe, clientSecret, achVerificationMethod, bankData });
      if (!collected) return;

      const saveRes = await billingApi.savePaymentMethod(
        buildAchSavePayload({ collected, makeDefault, achMandate })
      );
      if (saveRes?.success) {
        toast.success(
          'Bank account added. We\'ll send two micro-deposits in 1–2 business days for verification.'
        );
        resetBankForm();
        setMakeDefault(false);
        setAchMandateAccepted(false);
        await refetch();
      } else {
        toast.error(saveRes?.message || 'Failed to save payment method.');
      }
    } catch (err) {
      toast.error(friendlyAddError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading && paymentMethods.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-brand animate-spin mb-4" />
        <p className="text-fg-muted text-sm">Loading your payment methods...</p>
      </div>
    );
  }

  const baseTestId = dataTestId || 'profile-payment-section';
  const enableAutoRenewalLabel = renewalPreference.enabled ? 'Update Payment Method' : 'Enable Auto-Renewal';

  return (
    <div className="space-y-6" data-testid={baseTestId}>
      <h3 className={sectionHeading} data-testid="profile-payment-header">Payment Methods</h3>

      <div className={`${cardPadding} rounded-xl border space-y-2 ${cardSurfaceClassName}`} data-testid="profile-auto-renewal-card">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0 mr-3">
            <h4 className={cardTitle} data-testid="profile-auto-renewal-title">Auto Renewal</h4>
            <p className={cardDescription} data-testid="profile-auto-renewal-desc">
                Automatically renew your subscription
            </p>
          </div>
          {/* Shared Switch rather than a hand-rolled checkbox + peer-checked CSS, so it matches
              every other toggle in the app instead of being a different size and shape. */}
          <div
            className={`flex-shrink-0 ${
              autoRenewalLoading || savingAutoRenewal ? 'opacity-60 pointer-events-none' : ''
            }`}
            data-testid="profile-auto-renewal-toggle-label"
          >
            <Switch
              checked={renewalPreference.enabled}
              onCheckedChange={onAutoRenewalToggle}
              disabled={autoRenewalLoading || savingAutoRenewal}
              aria-label="Toggle auto renewal"
              dataTestId="profile-auto-renewal-toggle"
            />
          </div>
        </div>
        {renewalPreference.enabled && renewalPreference.paymentMethod?.label && (
          <div className="flex items-center gap-3" data-testid="profile-auto-renewal-info">
            <p className="text-xs text-brand font-medium" data-testid="profile-auto-renewal-method">
              Auto-charging {renewalPreference.paymentMethod.label}
            </p>
            <button
              type="button"
              onClick={() => {
                setEnableTokenChoice(null);
                setShowEnableDialog(true);
              }}
              className="text-xs text-brand hover:text-brand-hover font-medium underline"
              disabled={autoRenewalLoading || savingAutoRenewal}
              data-testid="profile-auto-renewal-change-button"
            >
              Change
            </button>
          </div>
        )}
        {autoRenewalReboundError && (
          <div className="flex items-start gap-2 rounded-xl bg-warning-bg border border-warning-line p-2 text-xs text-warning-fg" data-testid="profile-auto-renewal-rebound-error">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{autoRenewalReboundError}</span>
          </div>
        )}
      </div>

      <div className={segmentedTrack} data-testid="profile-payment-tabs">
        <button
          type="button"
          onClick={() => setActiveTab('card')}
          className={segmentedTab({ active: activeTab === 'card' })}
          data-testid="profile-payment-tab-card"
        >
          Pay by Card
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('ach')}
          className={segmentedTab({ active: activeTab === 'ach' })}
          data-testid="profile-payment-tab-ach"
        >
          ACH
        </button>
      </div>

      {error && (
        <div className="bg-danger-bg border border-danger-fg/30 text-danger-fg px-4 py-3 rounded-xl flex items-center gap-3 text-sm" data-testid="profile-payment-error">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      <PaymentMethodsList
        paymentMethods={paymentMethods}
        activeTab={activeTab}
        loading={loading}
        onVerify={setVerifyingMethod}
        onMakePrimary={setDefaultPaymentMethod}
        onDelete={setDeletingMethod}
      />

      {/* Inline Add New Payment Method Form — Stripe Elements for card; manual fields for ACH */}
      <div className="space-y-4 pt-2" data-testid="profile-add-payment-method-section">
        <h4 className="text-base font-semibold text-fg" data-testid="profile-add-payment-method-header">
          Add New Payment Method
        </h4>

        {activeTab === 'card' ? (
          <form onSubmit={submitCard} className="space-y-3 sm:space-y-4" data-testid="profile-add-card-form">
            <div>
              <Label htmlFor="inline-cardholder" className={labelClassName} data-testid="profile-add-card-name-label">
                Cardholder Name (optional)
              </Label>
              <Input
                id="inline-cardholder"
                placeholder="Name on card"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                className={`${fieldClassName} mt-1 text-sm`}
                disabled={isSubmitting}
                data-testid="profile-add-card-name-input"
              />
            </div>
            <div>
              <Label className={labelClassName} data-testid="profile-add-card-number-label">Card Number</Label>
              {/* Stripe iframe — PAN never crosses our origin. */}
              <StripeField className="mt-1" disabled={isSubmitting} dataTestId="profile-add-card-number-container">
                {(fieldProps) => (
                  <CardNumberElement
                    options={{ style: stripeElementStyle, placeholder: '1234 5678 9012 3456', disabled: isSubmitting }}
                    {...fieldProps}
                  />
                )}
              </StripeField>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className={labelClassName} data-testid="profile-add-card-expiry-label">Expiration</Label>
                <StripeField className="mt-1" disabled={isSubmitting} dataTestId="profile-add-card-expiry-container">
                  {(fieldProps) => (
                    <CardExpiryElement
                      options={{ style: stripeElementStyle, placeholder: 'MM/YY', disabled: isSubmitting }}
                      {...fieldProps}
                    />
                  )}
                </StripeField>
              </div>
              <div>
                <Label className={labelClassName} data-testid="profile-add-card-cvc-label">CVV</Label>
                <StripeField className="mt-1" disabled={isSubmitting} dataTestId="profile-add-card-cvc-container">
                  {(fieldProps) => (
                    <CardCvcElement
                      options={{ style: stripeElementStyle, placeholder: '123', disabled: isSubmitting }}
                      {...fieldProps}
                    />
                  )}
                </StripeField>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1" data-testid="profile-add-card-default-container">
              <Checkbox
                id="card-make-default"
                checked={makeDefault}
                onCheckedChange={(checked) => setMakeDefault(checked === true)}
                disabled={isSubmitting}
                data-testid="profile-add-card-default-checkbox"
              />
              <Label htmlFor="card-make-default" className={checkboxLabelClassName} data-testid="profile-add-card-default-label">
                Set as primary
              </Label>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={isSubmitting}
                className={saveButton}
                data-testid="profile-add-card-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" data-testid="profile-add-card-loading-spinner" />
                    Adding...
                  </>
                ) : (
                  'Add Payment Method'
                )}
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={submitBank} className="space-y-3 sm:space-y-4" data-testid="profile-add-ach-form">
            {/* Verification-method picker: Instant (Financial Connections) vs Manual (routing
                + account + micro-deposits). Same UI as the checkout dialog so the two ACH
                entry points stay consistent. */}
            <div>
              <Label className={labelClassName} data-testid="profile-add-ach-verify-method-label">
                How would you like to verify your bank account?
              </Label>
              {/* Shared RadioGroup (as ManageSeats' pay-method tiles) rather than native radios, so the
                  radio matches every other one in the app instead of the browser's own. */}
              <RadioGroup
                value={achVerificationMethod}
                onValueChange={setAchVerificationMethod}
                disabled={isSubmitting}
                className="space-y-2 mt-2"
                data-testid="profile-add-ach-verify-method-options"
              >
                <label className="flex items-start gap-3 rounded-xl border border-line p-3 cursor-pointer hover:bg-surface-field" aria-label="Connect your bank instantly" data-testid="profile-add-ach-verify-method-instant-label">
                  <RadioGroupItem
                    value="instant"
                    className="mt-1 shrink-0"
                    data-testid="profile-add-ach-verify-method-instant-radio"
                  />
                  <span className="text-sm">
                    <span className="block font-semibold text-fg">Connect your bank instantly</span>
                    <span className="block text-xs text-fg-muted">Log in to your bank — verified in seconds.</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 rounded-xl border border-line p-3 cursor-pointer hover:bg-surface-field" aria-label="Enter routing and account numbers" data-testid="profile-add-ach-verify-method-manual-label">
                  <RadioGroupItem
                    value="manual"
                    className="mt-1 shrink-0"
                    data-testid="profile-add-ach-verify-method-manual-radio"
                  />
                  <span className="text-sm">
                    <span className="block font-semibold text-fg">Enter routing &amp; account numbers</span>
                    <span className="block text-xs text-fg-muted">Verify with two small deposits in 1–2 business days.</span>
                  </span>
                </label>
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="inline-name-on-account" className={labelClassName} data-testid="profile-add-ach-name-label">
                Name on Account
              </Label>
              <Input
                id="inline-name-on-account"
                placeholder="John Doe"
                value={bankData.accountHolderName}
                onChange={(e) => setBankData({ ...bankData, accountHolderName: e.target.value })}
                className={`${fieldClassName} mt-1 text-sm`}
                disabled={isSubmitting}
                required
                data-testid="profile-add-ach-name-input"
              />
            </div>

            {achVerificationMethod === 'manual' && (
              <div data-testid="profile-add-ach-manual-fields">
                <div>
                  <Label htmlFor="inline-routing-number" className={labelClassName} data-testid="profile-add-ach-routing-label">
                    Routing Number
                  </Label>
                  <Input
                    id="inline-routing-number"
                    placeholder="123456789"
                    maxLength={9}
                    value={bankData.routingNumber}
                    onChange={(e) => setBankData({ ...bankData, routingNumber: e.target.value })}
                    className={`${fieldClassName} mt-1 text-sm`}
                    disabled={isSubmitting}
                    required
                    data-testid="profile-add-ach-routing-input"
                  />
                </div>
                <div>
                  <Label htmlFor="inline-account-number" className={labelClassName} data-testid="profile-add-ach-account-label">
                    Account Number
                  </Label>
                  <Input
                    id="inline-account-number"
                    placeholder="000123456789"
                    value={bankData.accountNumber}
                    onChange={(e) => setBankData({ ...bankData, accountNumber: e.target.value })}
                    className={`${fieldClassName} mt-1 text-sm`}
                    disabled={isSubmitting}
                    required
                    data-testid="profile-add-ach-account-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {/* Shared DropdownOptions (as Country / Main Contact) rather than native <select>s,
                      whose opened list is drawn by the OS instead of the app's themed menu. */}
                  <DropdownOptions
                    id="inline-holder-type"
                    label="Holder Type"
                    value={bankData.accountHolderType}
                    onValueChange={(value) => setBankData({ ...bankData, accountHolderType: value })}
                    options={[
                      { value: 'individual', label: 'Individual' },
                      { value: 'company', label: 'Company' },
                    ]}
                    disabled={isSubmitting}
                    dataTestId="profile-add-ach-holder-type"
                  />
                  <DropdownOptions
                    id="inline-account-type"
                    label="Account Type"
                    value={bankData.accountType}
                    onValueChange={(value) => setBankData({ ...bankData, accountType: value })}
                    options={[
                      { value: 'checking', label: 'Checking' },
                      { value: 'savings', label: 'Savings' },
                    ]}
                    disabled={isSubmitting}
                    dataTestId="profile-add-ach-account-type"
                  />
                </div>
                <p className="text-xs text-fg-muted" data-testid="profile-add-ach-manual-info">
                  We&apos;ll send two micro-deposits to this account in 1–2 business days. Verify
                  them with the descriptor starting with <code>SM</code> to activate the method.
                  {import.meta.env.DEV && (
                    <span className="block mt-1 text-warning-fg" data-testid="profile-add-ach-manual-dev-info">
                      Test mode: Use routing <code>110000000</code> and account <code>000123456789</code>.
                    </span>
                  )}
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1" data-testid="profile-add-ach-default-container">
              <Checkbox
                id="ach-make-default"
                checked={makeDefault}
                onCheckedChange={(checked) => setMakeDefault(checked === true)}
                disabled={isSubmitting}
                data-testid="profile-add-ach-default-checkbox"
              />
              <Label htmlFor="ach-make-default" className={checkboxLabelClassName} data-testid="profile-add-ach-default-label">
                Set as primary
              </Label>
            </div>

            {/* NACHA ACH debit authorization — text + hash come from the server so any revision
                flows automatically; the server re-verifies the hash on save and rejects with
                `ach_mandate_required` otherwise. */}
            <div className="space-y-2 rounded-xl border border-line bg-surface-field p-3" data-testid="profile-add-ach-mandate-container">
              <Label className={labelClassName} data-testid="profile-add-ach-mandate-label">Authorization</Label>
              <p className="text-xs leading-relaxed text-fg/80" data-testid="profile-add-ach-mandate-text">
                {achMandate?.text || 'Loading authorization text…'}
              </p>
              <div className="flex items-start gap-2 pt-1">
                <Checkbox
                  id="ach-mandate-accept"
                  className="mt-0.5"
                  checked={achMandateAccepted}
                  onCheckedChange={(checked) => setAchMandateAccepted(checked === true)}
                  disabled={!achMandate || isSubmitting}
                  data-testid="profile-add-ach-mandate-checkbox"
                />
                <Label htmlFor="ach-mandate-accept" className={checkboxLabelClassName} data-testid="profile-add-ach-mandate-accept-label">
                  I authorize the debit described above.
                </Label>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={isSubmitting || !achMandate || !achMandateAccepted}
                className={saveButton}
                data-testid="profile-add-ach-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" data-testid="profile-add-ach-loading-spinner" />
                    Adding...
                  </>
                ) : (
                  'Add Payment Method'
                )}
              </Button>
            </div>
          </form>
        )}
      </div>

      <VerifyAchDialog
        isOpen={!!verifyingMethod}
        onClose={() => setVerifyingMethod(null)}
        onVerify={verifyAch}
        methodToken={verifyingMethod?.token}
        dataTestId="profile-verify-ach-modal"
      />

      <DeletePaymentDialog
        deletingMethod={deletingMethod}
        onOpenChange={(open) => !open && setDeletingMethod(null)}
        onCancel={() => setDeletingMethod(null)}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />

      <EnableAutoRenewalDialog
        open={showEnableDialog}
        onOpenChange={(open) => !open && setShowEnableDialog(false)}
        onCancel={() => setShowEnableDialog(false)}
        autoRenewal={renewalPreference}
        enableTokenChoice={enableTokenChoice}
        setEnableTokenChoice={setEnableTokenChoice}
        savingAutoRenewal={savingAutoRenewal}
        onConfirm={confirmEnableAutoRenewal}
        enableAutoRenewalLabel={enableAutoRenewalLabel}
      />

      <DisableAutoRenewalDialog
        open={showDisableDialog}
        onOpenChange={(open) => !open && setShowDisableDialog(false)}
        onCancel={() => setShowDisableDialog(false)}
        onConfirm={confirmDisableAutoRenewal}
        savingAutoRenewal={savingAutoRenewal}
      />
    </div>
  );
}
